import React, { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { actions } from '../redux/ProductReducer';
import InternalContainer from '../../layout/containers/Internal';
import { Tab, Tabs } from 'react-bootstrap';
import { Box, Loader } from '../../../common/components';
import CountryMappingForm from '../components/CountryMappingForm/CountryMappingForm';
import {
  getDestinationsMappings,
  getFormattedRegions,
  getSchemeList,
} from '../helpers/countryMappingHelpers';

const CountryMappingContainer = ({
  params,
  getProduct,
  getDestinations,
  getInsurerDestinationMapping,
  updateDestinationsMapping,
}) => {
  const { productId, insurerId } = params;

  const isSchemeGroup = ['FreeSpirit', 'Free2Travel'].includes(insurerId);

  const {
    product,
    isLoading,
    topLevelArea: { singleSchemeMeta, annualSchemeMeta },
    destinations,
  } = useSelector((state) => state.productManagement);

  const singleSchemeList = getSchemeList(
    isSchemeGroup,
    singleSchemeMeta?.[insurerId]?.schemes ?? [],
  );
  const annualSchemeList = getSchemeList(
    isSchemeGroup,
    annualSchemeMeta?.[insurerId]?.schemes ?? [],
  );

  const currentProductId = product?.data?.id;
  useEffect(
    () => {
      if (!currentProductId || currentProductId !== productId) {
        getProduct(productId);
      }
    },
    [productId, currentProductId, getProduct],
  );

  useEffect(
    () => {
      getInsurerDestinationMapping(insurerId, isSchemeGroup);
    },
    [insurerId, isSchemeGroup, getInsurerDestinationMapping],
  );

  useEffect(
    () => {
      if (!destinations) {
        getDestinations();
      }
    },
    [destinations, getDestinations],
  );

  const updateCountryMapping = (formValues) => {
    const sortedDestinationMapping = formValues.destinations_mapping.sort(
      (destA, destB) => {
        return destA.country < destB.country ? -1 : 1;
      },
    );

    const rules = {
      exclude_destinations: formValues.exclude_destinations,
    };

    const values = {
      meta: {
        parameters: {
          destinations_mapping: sortedDestinationMapping?.reduce((carry, dest) => {
            if (dest.country && dest.status) {
              carry[dest.country] = dest.status;

              return carry;
            }
          }, {}),
        },
        rules: rules,
        schemes: formValues.schemes,
      },
    };

    return updateDestinationsMapping(values, formValues.trip_type, isSchemeGroup);
  };

  const singleSchemeRegions = getFormattedRegions(
    singleSchemeMeta?.[insurerId]?.parameters?.regions ?? [],
  );

  const singleSchemeDestinationMapping =
    singleSchemeMeta?.[insurerId]?.parameters?.destination_mapping ?? {};

  const singleSchemeDestinationRulesMapping =
    singleSchemeMeta?.[insurerId]?.parameters?.rules ?? {};

  const singleSchemeMappings = useMemo(
    () =>
      getDestinationsMappings(
        singleSchemeDestinationMapping,
        singleSchemeDestinationRulesMapping,
        destinations,
      ),
    [singleSchemeDestinationMapping, singleSchemeDestinationRulesMapping, destinations],
  );

  const annualSchemeRegions = getFormattedRegions(
    annualSchemeMeta?.[insurerId]?.parameters?.regions ?? [],
  );

  const annualSchemeDestinationMapping =
    annualSchemeMeta?.[insurerId]?.parameters?.destination_mapping ?? {};

  const annualSchemeDestinationRulesMapping =
    annualSchemeMeta?.[insurerId]?.parameters?.rules ?? {};

  const annualSchemeMappings = useMemo(
    () =>
      getDestinationsMappings(
        annualSchemeDestinationMapping,
        annualSchemeDestinationRulesMapping,
        destinations,
      ),
    [annualSchemeDestinationMapping, annualSchemeDestinationRulesMapping, destinations],
  );

  if (isLoading || (!singleSchemeMappings && !annualSchemeMappings)) {
    return (
      <InternalContainer title={'Country Mapping'}>
        <Loader />
      </InternalContainer>
    );
  }

  return (
    <InternalContainer title={'Country Mapping'}>
      <Tabs defaultActiveKey={1} id="uncontrolled-tab-example" unmountOnExit={true}>
        <Tab eventKey={1} title="Single Trip">
          {singleSchemeMappings?.mappings && !singleSchemeMappings?.mappings?.length && (
            <Box>No Destination Mapping found</Box>
          )}
          {!!singleSchemeMappings?.mappings?.length && !!destinations?.length && (
            <CountryMappingForm
              destinationsMapping={singleSchemeMappings?.mappings}
              unassignedCountries={singleSchemeMappings?.unassignedCountries}
              excludeCountries={singleSchemeMappings?.excludeCountries}
              statuses={singleSchemeRegions}
              allDestinations={destinations}
              tripType={'single'}
              numSchemes={singleSchemeList?.length}
              schemesToUpdate={singleSchemeList}
              updateCountryMapping={updateCountryMapping}
              formName="CountryMappingFormSingle"
            />
          )}
        </Tab>
        <Tab eventKey={2} title="Annual Trip">
          {annualSchemeMappings?.mappings && !annualSchemeMappings?.mappings?.length && (
            <Box>No Destination Mapping found</Box>
          )}
          {!!annualSchemeMappings?.mappings?.length && !!destinations?.length && (
            <CountryMappingForm
              destinationsMapping={annualSchemeMappings?.mappings}
              unassignedCountries={annualSchemeMappings?.unassignedCountries}
              excludeCountries={annualSchemeMappings?.excludeCountries}
              statuses={annualSchemeRegions}
              allDestinations={destinations}
              tripType={'annual'}
              numSchemes={annualSchemeList?.length}
              schemesToUpdate={annualSchemeList}
              updateCountryMapping={updateCountryMapping}
              formName="CountryMappingFormAnnual"
            />
          )}
        </Tab>
      </Tabs>
    </InternalContainer>
  );
};

export default connect(
  null,
  {
    getProduct: actions.getProduct,
    updateDestinationsMapping: actions.updateDestinationsMapping,
    getInsurerDestinationMapping: actions.getInsurerDestinationMapping,
    getDestinations: actions.getDestinations,
  },
)(CountryMappingContainer);
